import React, { useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Globe } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { key: "about", href: "#about" },
  { key: "experiences", href: "#experiences" },
  { key: "gallery", href: "#gallery" },
  { key: "booking", href: "#booking" },
];

export const Header = () => {
  const { t, lang, setLang, languages } = useLang();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-colors duration-500",
        scrolled
          ? "bg-[#F8F5F0]/90 backdrop-blur-md border-b border-[#2C302E]/10"
          : "bg-transparent"
      )}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="brand-logo"
          className={cn(
            "font-serif text-xl md:text-2xl tracking-tight leading-none transition-colors",
            scrolled ? "text-[#2C302E]" : "text-[#F8F5F0]"
          )}
        >
          Villa Sućuraj
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.key}
              href={item.href}
              data-testid={`nav-link-${item.key}`}
              className={cn(
                "text-xs uppercase tracking-[0.22em] font-medium transition-colors",
                scrolled
                  ? "text-[#2C302E] hover:text-[#C05C3D]"
                  : "text-[#F8F5F0]/90 hover:text-white"
              )}
            >
              {t.nav[item.key]}
            </a>
          ))}
        </nav>

        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid="language-selector"
            className={cn(
              "flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-medium px-3 py-2 border transition-colors",
              scrolled
                ? "text-[#2C302E] border-[#2C302E]/30 hover:border-[#C05C3D] hover:text-[#C05C3D]"
                : "text-[#F8F5F0] border-[#F8F5F0]/40 hover:border-[#F8F5F0]"
            )}
          >
            <Globe className="h-3.5 w-3.5" />
            <span data-testid="current-lang">{lang.toUpperCase()}</span>
            <ChevronDown className="h-3 w-3" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-[#F8F5F0] border border-[#2C302E]/15 rounded-none min-w-[140px]"
          >
            {languages.map((l) => (
              <DropdownMenuItem
                key={l.code}
                data-testid={`lang-option-${l.code}`}
                onClick={() => setLang(l.code)}
                className={cn(
                  "rounded-none text-xs uppercase tracking-[0.18em] cursor-pointer py-2",
                  lang === l.code ? "text-[#C05C3D] font-semibold" : "text-[#2C302E]"
                )}
              >
                {l.label} · {l.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
